/**
 * MediaContext
 *
 * Context para manejo centralizado de media items en el sistema
 * Basado en la estructura de owly-sgi
 *
 * @see /docs/05-modules/sgi/media-management.md
 */

import React, { createContext, useContext, useState, ReactNode } from 'react';

// Media Item Interface basada en owly-sgi
export interface MediaItem {
  reference: string;      // Format: "{type}-{counter}" e.g., "image-1", "rotator-3"
  type: MediaType;        // One of the 10 media types
  url: string;            // URL to the media file (S3 or external URL)
  extension?: string;     // File extension (jpg, png, glb, zip, etc.)
  fileContent?: string;   // Base64 encoded file content (during upload)
}

// Media Types - 10 categorías como en owly-sgi
export type MediaType =
  | 'image'           // Imágenes genéricas
  | 'rotator'         // Vistas 360° (OrbitView)
  | 'virtualTour'     // Tours virtuales
  | 'isometric'       // Vistas isométricas
  | 'layout'          // Planos arquitectónicos
  | 'measurements'    // Diagramas de medidas
  | 'floorPlan'       // Renders de planta
  | 'view'            // Vistas generales
  | 'map'             // Mapas de ubicación
  | 'other';          // Otros tipos

interface MediaContextValue {
  mediaItems: MediaItem[];
  addMedia: (media: MediaItem) => void;
  deleteMedia: (mediaReference: string) => void;
  getMediaReference: (mediaType: MediaType) => string;
  updateMedia: (mediaReference: string, updatedMedia: MediaItem) => void;
  setMediaItems: React.Dispatch<React.SetStateAction<MediaItem[]>>;
}

const MediaContext = createContext<MediaContextValue | undefined>(undefined);

interface MediaProviderProps {
  children: ReactNode;
}

export const MediaProvider: React.FC<MediaProviderProps> = ({ children }) => {
  const [mediaItems, setMediaItems] = useState<MediaItem[]>([]);

  // Add a new media item
  const addMedia = (media: MediaItem) => {
    setMediaItems((prevMediaItems) => {
      return [...prevMediaItems, media];
    });
  };

  // Remove a media item by reference
  const deleteMedia = (mediaReference: string) => {
    setMediaItems((prevMediaItems) => {
      return prevMediaItems.filter((media) => media.reference !== mediaReference);
    });
  };

  // Generate next reference number for a media type
  // Counts existing items of same type and increments
  const getMediaReference = (mediaType: MediaType): string => {
    let counter = 0;
    mediaItems.forEach((item) => {
      if (item.type === mediaType) {
        counter++;
      }
    });
    counter++;
    return `${mediaType}-${counter}`;
  };

  // Update existing media item
  const updateMedia = (mediaReference: string, updatedMedia: MediaItem) => {
    setMediaItems((prevMediaItems) => {
      const updatedMediaItems = prevMediaItems.map((media) => {
        if (media.reference === mediaReference) {
          return updatedMedia;
        }
        return media;
      });
      return updatedMediaItems;
    });
  };

  return (
    <MediaContext.Provider
      value={{
        mediaItems,
        addMedia,
        deleteMedia,
        getMediaReference,
        updateMedia,
        setMediaItems,
      }}
    >
      {children}
    </MediaContext.Provider>
  );
};

// Custom hook to use the MediaContext
export const useMedia = (): MediaContextValue => {
  const context = useContext(MediaContext);
  if (context === undefined) {
    throw new Error('useMedia must be used within a MediaProvider');
  }
  return context;
};

export default MediaContext;
